import { useEffect, useState } from "react";
import { Check, Facebook, Link2, Linkedin, MessageCircle, Share2, X } from "lucide-react";

type ShareBarProps = {
  title?: string;
};

function XIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

export function ShareBar({ title }: ShareBarProps) {
  const [url, setUrl] = useState("");
  const [shareTitle, setShareTitle] = useState(title ?? "");
  const [open, setOpen] = useState(true);
  const [copied, setCopied] = useState(false);
  const [canNativeShare, setCanNativeShare] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    setUrl(window.location.href);
    if (!title) setShareTitle(document.title);
    setCanNativeShare(typeof navigator !== "undefined" && !!navigator.share);
  }, [title]);

  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(shareTitle);

  const links = [
    {
      name: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      Icon: Facebook,
      className: "bg-[#1877F2] hover:bg-[#166fe0]",
    },
    {
      name: "X",
      href: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
      Icon: XIcon,
      className: "bg-black hover:bg-neutral-800",
    },
    {
      name: "WhatsApp",
      href: `https://api.whatsapp.com/send?text=${encodedTitle}%20${encodedUrl}`,
      Icon: MessageCircle,
      className: "bg-[#25D366] hover:bg-[#20bd5a]",
    },
    {
      name: "LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      Icon: Linkedin,
      className: "bg-[#0A66C2] hover:bg-[#0958a8]",
    },
  ];

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(url || window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  }

  async function nativeShare() {
    try {
      await navigator.share({ title: shareTitle, url: url || window.location.href });
    } catch {
      /* user dismissed */
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open share bar"
        title="Share this article"
        className="fixed bottom-6 right-6 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground shadow-xl transition hover:scale-105"
      >
        <Share2 className="h-5 w-5" aria-hidden="true" />
      </button>
    );
  }

  return (
    <div
      role="region"
      aria-label="Share this article"
      className="fixed bottom-4 left-1/2 z-40 flex -translate-x-1/2 items-center gap-2 rounded-full border border-white/10 bg-navy/90 px-3 py-2 shadow-2xl backdrop-blur-md sm:gap-3 sm:px-4"
    >
      <span className="hidden pl-2 pr-1 text-xs font-medium uppercase tracking-wider text-accent sm:inline">
        Share
      </span>
      {links.map(({ name, href, Icon, className }) => (
        <a
          key={name}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Share on ${name}`}
          title={`Share on ${name}`}
          className={`flex h-10 w-10 items-center justify-center rounded-full text-white shadow-md transition hover:scale-110 ${className}`}
        >
          <Icon className="h-5 w-5" />
        </a>
      ))}
      <button
        type="button"
        onClick={copyLink}
        aria-label={copied ? "Link copied" : "Copy link"}
        title={copied ? "Link copied" : "Copy link"}
        className="flex h-10 w-10 items-center justify-center rounded-full bg-accent text-accent-foreground shadow-md transition hover:scale-110"
      >
        {copied ? (
          <Check className="h-5 w-5" aria-hidden="true" />
        ) : (
          <Link2 className="h-5 w-5" aria-hidden="true" />
        )}
      </button>
      {canNativeShare && (
        <button
          type="button"
          onClick={nativeShare}
          aria-label="Share via device"
          title="Share via device"
          className="flex h-10 w-10 items-center justify-center rounded-full border border-accent/40 text-accent shadow-md transition hover:scale-110 sm:hidden"
        >
          <Share2 className="h-5 w-5" aria-hidden="true" />
        </button>
      )}
      <button
        type="button"
        onClick={() => setOpen(false)}
        aria-label="Hide share bar"
        title="Hide share bar"
        className="ml-1 flex h-8 w-8 items-center justify-center rounded-full text-white/70 transition hover:bg-card/10 hover:text-white"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}

export default ShareBar;
